// pages/planning-map.js
import dynamic from "next/dynamic";
import Link from "next/link";
import React, { Suspense } from "react";
import Meta from "../components/Meta";
import CouncilCoverageNotice from "../components/planning/CouncilCoverageNotice";
import { PlanningContextProvider } from "../components/planning/state/PlanningContext";
import { PlanningSearchProvider } from "../components/planning/state/PlanningSearchContext";
import {
  AGGREGATE_COUNCIL_SLUG,
  DEFAULT_COUNCIL_SLUG,
  getCouncilConfig,
  isAggregateCouncilScope,
  isLegacyCouncil,
  LONDON_REGION_CONFIG,
} from "../lib/councils";
import {
  PLANNING_COVERAGE_LINKS,
  PLANNING_CURRENT_COVERAGE_COPY,
  getPlanningSeoDescription,
  getPlanningSeoPlaceName,
} from "../lib/planningSeo";

// Planning Updates themed skeleton
const MapSkeleton = () => (
  <div className="pr-map-loading flex h-full w-full items-center justify-center bg-[var(--c-bg)]">
    <div className="flex w-full max-w-xs flex-col items-center gap-5 px-6 text-center">
      <p className="pr-kicker pr-kicker--accent">CityScope &middot; Planning Updates</p>
      <p className="font-['Newsreader'] text-2xl font-medium leading-tight text-[var(--c-text-primary)]">
        Preparing the map
      </p>
      <div className="h-px w-full overflow-hidden bg-[var(--c-border)]">
        <div className="pr-map-loading-rule h-full w-1/3 bg-[var(--c-accent)]" />
      </div>
      <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[0.14em] text-[var(--c-text-tertiary)]">
        Drawing updates onto the map
      </p>
    </div>
  </div>
);

// Dynamically import the PlanningMapGL component and disable SSR
const PlanningMapGL = dynamic(() => import("../components/planning/PlanningMapGL"), {
  ssr: false,
  loading: () => <MapSkeleton />,
});

const getPlanningMapPath = (council, councilSlug = DEFAULT_COUNCIL_SLUG) =>
  councilSlug === 'london' ? '/planning-map/london' : isAggregateCouncilScope(councilSlug)
    ? "/planning-map"
    : council && !isLegacyCouncil(council.slug)
    ? `/planning-map/${council.shortCode}`
    : council
    ? `/planning-map/${council.shortCode}`
    : "/planning-map";

const getPlanningMapAreaPath = (areaId = null, fallbackCouncilSlug = DEFAULT_COUNCIL_SLUG) => {
  const raw = String(areaId || "").trim().toLowerCase();
  const [councilSlug, areaSlug] = raw.split(":");
  const council = getCouncilConfig(councilSlug || fallbackCouncilSlug);
  if (!council || !areaSlug) return getPlanningMapPath(council, fallbackCouncilSlug);
  return `/planning-map/${council.shortCode}/${areaSlug}`;
};

const buildPlanningMapStructuredData = (council, councilSlug = DEFAULT_COUNCIL_SLUG) => {
  const aggregateScope = isAggregateCouncilScope(councilSlug);
  const placeName = getPlanningSeoPlaceName(aggregateScope ? AGGREGATE_COUNCIL_SLUG : council?.slug || DEFAULT_COUNCIL_SLUG);
  const path = getPlanningMapPath(council, councilSlug);
  const url = `https://www.city-scope.co.uk${path}`;
  const description = getPlanningSeoDescription({
    placeName,
    surface: "map",
    aggregate: aggregateScope,
  });
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: `${placeName} Planning Map`,
    description,
    isPartOf: { "@id": "https://www.city-scope.co.uk/#website" },
    about: [
      `${placeName} planning applications`,
      ...PLANNING_COVERAGE_LINKS.map((link) => link.label),
      "Planning map",
      "Planning permission",
      "Local development proposals",
    ],
  };
};

// Civic-noir themed error boundary
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(err, info) {
    console.error("PlanningMap encountered an error", err, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-6 bg-[var(--c-bg)] p-8 text-center">
          <div className="max-w-md">
            <p className="pr-kicker pr-kicker--accent">CityScope &middot; Planning Updates</p>
            <h2 className="mt-4 font-['Newsreader'] text-3xl font-medium leading-tight text-[var(--c-text-primary)]">
              The map could not be drawn
            </h2>
            <hr className="pr-double-rule mt-5" aria-hidden="true" />
            <p className="mt-5 text-sm leading-relaxed text-[var(--c-text-secondary)]">
              The interactive map encountered an issue during initialisation. Refresh the page to try again.
            </p>
            <button
              onClick={() => location.assign(location.href)}
              className="pr-button mt-6"
            >
              Reload map
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function PlanningMapPage({ councilSlug = AGGREGATE_COUNCIL_SLUG, areaId = null }) {
  const aggregateScope = isAggregateCouncilScope(councilSlug);
  const council = aggregateScope
    ? null
    : councilSlug === 'london' ? LONDON_REGION_CONFIG
    : getCouncilConfig(councilSlug) || getCouncilConfig(DEFAULT_COUNCIL_SLUG);
  const placeName = getPlanningSeoPlaceName(aggregateScope ? AGGREGATE_COUNCIL_SLUG : council?.slug || DEFAULT_COUNCIL_SLUG);
  const contextCouncilSlug = aggregateScope ? AGGREGATE_COUNCIL_SLUG : council?.slug;
  const mapPath = areaId
    ? getPlanningMapAreaPath(areaId, councilSlug)
    : getPlanningMapPath(council, councilSlug);
  const updatesPath = aggregateScope
    ? "/planning-updates"
    : council && !isLegacyCouncil(council.slug)
    ? `/planning-updates/${council.shortCode}`
    : council
    ? `/planning-updates/${council.shortCode}`
    : "/planning-updates";
  const structuredData = buildPlanningMapStructuredData(council, councilSlug);

  return (
    <>
      <Meta
        title={`${placeName} Planning Map | City Scope`}
        description={getPlanningSeoDescription({
          placeName,
          surface: "map",
          aggregate: aggregateScope,
        })}
        canonical={mapPath}
        noindex={Boolean(council && !council.publicEnabled)}
        nofollow={Boolean(council && !council.publicEnabled)}
        structuredData={structuredData}
      />
      <PlanningContextProvider
        key={`${contextCouncilSlug}:${areaId || "all"}`}
        councilSlug={contextCouncilSlug}
        areaId={areaId}
        disableDataBootstrap
      >
        <PlanningSearchProvider skipUnfilteredRemoteBootstrap>
          <div className="planning-register relative h-screen w-full overflow-hidden bg-[var(--c-bg)]" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        {/* Static HTML for crawlers/agents; visually hidden so the map remains unobstructed. */}
        <aside
          aria-hidden="true"
          inert={true}
          className="pointer-events-none absolute h-px w-px overflow-hidden whitespace-nowrap border-0 p-0 [clip-path:inset(50%)] [clip:rect(0,0,0,0)]"
        >
          <div>
            <h1 className="text-sm font-bold text-[var(--c-text-primary)] font-['Space_Grotesk'] sm:text-base">
              {placeName} Planning Map
            </h1>
            <p className="mt-1 line-clamp-3 text-xs leading-relaxed text-[var(--c-text-secondary)] font-['DM_Mono'] sm:line-clamp-none">
              Explore {placeName} planning applications by location, address, category, and status. For a fully crawlable, text-first index of applications, use Planning Updates. {aggregateScope ? PLANNING_CURRENT_COVERAGE_COPY : null}
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold font-['DM_Mono']">
              <Link
                href={updatesPath}
                className="rounded-md bg-[var(--c-bg)] px-3 py-1.5 text-[var(--c-accent)] hover:underline border border-[var(--c-border)]"
              >
                {placeName} Planning Updates
              </Link>
              {council?.slug === "edinburgh" ? (
                <>
                  <Link
                    href="/planning-updates?category=short_term_lets"
                    className="rounded-md bg-[var(--c-bg)] px-3 py-1.5 text-orange-300 hover:underline border border-[rgba(249,115,22,0.28)]"
                  >
                    Edinburgh Short Lets
                  </Link>
                  <Link
                    href="/short-lets-report"
                    className="rounded-md bg-orange-300 px-3 py-1.5 text-stone-950 hover:underline border border-orange-200"
                  >
                    Short-Let Watch
                  </Link>
                </>
              ) : null}
              <Link
                href="/planning-analytics"
                className="hidden rounded-md bg-[var(--c-bg)] px-3 py-1.5 text-[var(--c-text-primary)] hover:underline border border-[var(--c-border)] sm:inline-flex"
              >
                Planning Analytics
              </Link>
              {aggregateScope
                ? PLANNING_COVERAGE_LINKS.map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      className="hidden rounded-md bg-[var(--c-bg)] px-3 py-1.5 text-[var(--c-text-primary)] hover:underline border border-[var(--c-border)] sm:inline-flex"
                    >
                      {link.label}
                    </Link>
                  ))
                : null}
            </div>
          </div>
        </aside>

        <Suspense fallback={<MapSkeleton />}>
          <ErrorBoundary>
            <PlanningMapGL />
          </ErrorBoundary>
        </Suspense>

        {/* Register masthead — editorial identity anchored under the nav */}
        <div
          className="pr-map-masthead"
          style={{ top: 'calc(var(--navbar-height, 64px) + var(--planning-region-rail-height, 0px) + 0.75rem)' }}
          aria-hidden="true"
        >
          <div className="pr-map-masthead-card">
            <p className="pr-map-masthead-kicker">CityScope &middot; Planning Updates</p>
            <p className="pr-map-masthead-title">
              {placeName} <em>on the map</em>
            </p>
            <p className="pr-map-masthead-meta hidden sm:block">
              {council?.releaseStage === 'official_records'
                ? 'Available council records, with mapped locations'
                : 'Planning records, with mapped locations'}
            </p>
          </div>
        </div>

        {/* Register-style map attribution colophon */}
        <footer className="pointer-events-none fixed inset-x-0 bottom-0 z-[1156] flex flex-col items-center">
          {council?.releaseStage === 'official_records' || council?.sourceCoverage ? (
            <div className="pointer-events-auto mb-1 w-[calc(100%-1rem)] max-w-lg">
              <CouncilCoverageNotice council={council} compact mapView />
            </div>
          ) : null}
          <div
            className="pointer-events-auto max-w-[calc(100vw-1rem)] overflow-hidden border border-b-0 border-[var(--c-border)] bg-[var(--c-surface)] px-2.5 py-1 font-['DM_Mono'] text-[9px] uppercase tracking-[0.08em] leading-none text-[var(--c-text-tertiary)] sm:px-3 sm:py-1.5 sm:text-[10px] sm:leading-normal"
            role="contentinfo"
          >
            <span className="inline-flex items-center gap-1.5 sm:hidden">
              <span className="h-[5px] w-[5px] bg-[var(--c-accent)]"></span>
              Map data © OSM
            </span>
            <span className="hidden items-center gap-1.5 sm:inline-flex">
              <span className="h-[5px] w-[5px] bg-[var(--c-accent)]"></span>
              Rendering
              {' '}
              <a
                href="https://maplibre.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--c-text-secondary)] transition-colors hover:text-[var(--c-accent)]"
              >
                MapLibre GL
              </a>
              <span aria-hidden="true">·</span>
              Tiles ©
              {' '}
              <a
                href={council?.region === 'london' ? 'https://www.openstreetmap.org/copyright' : 'https://www.maptiler.com'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--c-text-secondary)] transition-colors hover:text-[var(--c-accent)]"
              >
                {council?.region === 'london' ? 'OpenStreetMap' : 'MapTiler'}
              </a>
              <span aria-hidden="true">·</span>
              ©
              {' '}
              <a
                href="https://www.openstreetmap.org/copyright"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--c-text-secondary)] transition-colors hover:text-[var(--c-accent)]"
              >
                OpenStreetMap
              </a>
            </span>
            {council?.region !== 'london' && <span className="ml-1 inline-flex gap-1 leading-normal">
              · Locations:
              <a href="https://data.spatialhub.scot/dataset/planning_applications_official-is" target="_blank" rel="noopener noreferrer" className="hover:underline">Improvement Service</a>
              <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="hover:underline">(OGL)</a>
            </span>}
          </div>
          </footer>
        </div>
        </PlanningSearchProvider>
      </PlanningContextProvider>
    </>
  );
}
